import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import Faqs from "./Faqs";
import HomePage from "./HomePage";
import Contact from "./Contact";
import WelcomePage from "./WelcomePage";
import WorkspaceFaqPage from "./WorkspaceFaqPage";
import WorkspaceAccountPage from "./WorkspaceAccountPage";
import WorkspacePrivacyPage from "./WorkspacePrivacyPage";
import WorkspaceTermsPage from "./WorkspaceTermsPage";

import { getToken, handleUnauthorizedResponse } from "./authStorage";

// Legacy first portal from the old logged-in experience is intentionally disabled.
// The workspace portal at /welcome is now the only active authenticated entry point.

const API_BASE = process.env.REACT_APP_API_BASE || "/api";

function AuthLoading() {
  return (
    <div className="app-container auth-loading">
      <p>Checking session...</p>
    </div>
  );
}

function RequireAuth({ children, isAuthenticated }) {
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

export default function App() {
  const [authState, setAuthState] = useState({
    ready: false,
    isAuthenticated: false,
  });

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setAuthState({ ready: true, isAuthenticated: false });
      return undefined;
    }

    let cancelled = false;

    fetch(`${API_BASE}/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (handleUnauthorizedResponse(response)) {
          throw new Error("Stored token is invalid");
        }
        if (!response.ok) {
          throw new Error("Could not validate stored token");
        }
        if (!cancelled) {
          setAuthState({ ready: true, isAuthenticated: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAuthState({ ready: true, isAuthenticated: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!authState.ready) {
    return <AuthLoading />;
  }

  return (
    <Router>
      <Routes>
        {/* Public Landing */}
        <Route
          path="/"
          element={
            authState.isAuthenticated ? (
              <Navigate to="/welcome" replace />
            ) : (
              <HomePage />
            )
          }
        />
        <Route
          path="/login"
          element={authState.isAuthenticated ? <Navigate to="/welcome" replace /> : <HomePage defaultModal="login" />}
        />
        <Route
          path="/signup"
          element={authState.isAuthenticated ? <Navigate to="/welcome" replace /> : <HomePage defaultModal="signup" />}
        />
        <Route path="/privacy" element={<WorkspacePrivacyPage />} />
        <Route path="/terms" element={<WorkspaceTermsPage />} />
        <Route path="/faqs" element={<Faqs />} />
        <Route path="/contact" element={<Contact />} />
        <Route
          path="/account"
          element={
            <RequireAuth isAuthenticated={authState.isAuthenticated}>
              <WorkspaceAccountPage />
            </RequireAuth>
          }
        />
        <Route
          path="/welcome"
          element={
            <RequireAuth isAuthenticated={authState.isAuthenticated}>
              <WelcomePage />
            </RequireAuth>
          }
        />
        <Route
          path="/workspace-faq"
          element={
            <RequireAuth isAuthenticated={authState.isAuthenticated}>
              <WorkspaceFaqPage />
            </RequireAuth>
          }
        />

        {/* Protected Convert Page */}
        <Route
          path="/convert"
          element={
            <RequireAuth isAuthenticated={authState.isAuthenticated}>
              <Navigate to="/welcome" replace />
            </RequireAuth>
          }
        />


        {/* Catch All */}
        <Route path="*" element={<Navigate to={authState.isAuthenticated ? "/welcome" : "/"} replace />} />
      </Routes>
    </Router>
  );
}
